import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Interactive Scopes Editor Controller
 *
 * Provides a visual interface for configuring ReportDataSource scopes.
 * Replaces manual JSON editing with dropdowns and form inputs.
 */
export default class extends Controller {
  static targets = [
    "hiddenField",
    "scopeContainer",
    "organizationScope",
    "networkScope",
    "userScope",
    "systemScope",
    "defaultScope",
    "jsonPreview"
  ]

  static values = {
    modelClass: String,
    tableName: String,
    columns: Array,
    associations: Array,
    namedScopes: Array,
    validateUrl: String
  }

  connect() {
    this.scopes = this.parseScopes()
    this.validationTimeouts = {}
    this.validationStates = {}
    this.renderAllScopes()
  }

  parseScopes() {
    try {
      const value = this.hiddenFieldTarget.value
      return value ? JSON.parse(value) : {}
    } catch {
      return {}
    }
  }

  renderAllScopes() {
    // Note: 'default' scope removed - it's rarely needed and causes confusion
    // If a scope type isn't configured, it returns all records (no filtering)
    const scopeTypes = ['organization', 'network', 'user', 'system']
    scopeTypes.forEach(type => {
      const target = this[`${type}ScopeTarget`]
      if (target) {
        this.renderScopeConfig(target, type, this.scopes[type])
      }
    })
  }

  renderScopeConfig(container, scopeType, config) {
    const scopeMode = this.determineScopeMode(config)
    const validationState = this.validationStates[scopeType]

    // Plain English labels for each mode
    const modeLabels = {
      none: `Disabled (show all records regardless of ${scopeType})`,
      named_scope: 'Use a predefined filter',
      where: 'Simple filter (match a column to user context)',
      advanced: 'Advanced filter (through related tables)'
    }

    container.innerHTML = `
      <div class="space-y-3">
        <div class="flex items-center gap-2">
          <select class="select select-bordered select-sm flex-1"
                  data-scope-type="${scopeType}"
                  data-action="change->scopes-editor#changeScopeMode">
            <option value="none" ${scopeMode === 'none' ? 'selected' : ''}>${modeLabels.none}</option>
            <option value="named_scope" ${scopeMode === 'named_scope' ? 'selected' : ''}>${modeLabels.named_scope}</option>
            <option value="where" ${scopeMode === 'where' ? 'selected' : ''}>${modeLabels.where}</option>
            <option value="advanced" ${scopeMode === 'advanced' ? 'selected' : ''}>${modeLabels.advanced}</option>
          </select>
          <button type="button"
                  class="btn btn-ghost btn-sm btn-square"
                  data-scope-type="${scopeType}"
                  data-action="click->scopes-editor#testScope"
                  title="${i18n.t('scopes_editor.test_scope')}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </button>
          <div data-validation-indicator="${scopeType}" class="flex items-center gap-1">
            ${this.renderValidationIndicator(validationState)}
          </div>
        </div>
        <div class="scope-config-details pl-4 border-l-2 border-base-300">
          ${this.renderScopeDetails(scopeType, scopeMode, config)}
        </div>
      </div>
    `
  }

  renderValidationIndicator(state) {
    if (!state) return ''

    if (state.pending) {
      return '<span class="loading loading-spinner loading-xs"></span>'
    }

    if (state.valid) {
      // Show record count if available
      if (state.scoped_count !== undefined && state.total_count !== undefined) {
        return `<span class="badge badge-success badge-sm gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  ${state.scoped_count.toLocaleString()} of ${state.total_count.toLocaleString()}
                </span>
                <span class="text-xs text-gray-500">(${state.percentage}%)</span>`
      }
      return '<span class="badge badge-success badge-sm">Valid</span>'
    }

    if (state.valid === false) {
      const errorMsg = state.error || (state.errors && state.errors.join(', ')) || 'Invalid'
      return `<span class="badge badge-error badge-sm">Invalid</span>
              <span class="tooltip tooltip-left cursor-help" data-tip="${this.escapeHtml(errorMsg)}">
                <svg class="w-4 h-4 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </span>`
    }

    return ''
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML.replace(/"/g, '&quot;')
  }

  determineScopeMode(config) {
    if (config === null || config === undefined) return 'none'
    if (typeof config === 'string') return 'named_scope'
    if (typeof config === 'object') {
      // Check for property existence, not truthiness (empty string is valid for joins)
      if ('joins' in config || config.merge) return 'advanced'
      if (config.where && Object.keys(config.where).length > 0) return 'where'
      if (config.scope) return 'named_scope'
      // If object has 'where' key (even empty), treat as 'where' mode
      if ('where' in config) return 'where'
    }
    return 'none'
  }

  renderScopeDetails(scopeType, mode, config) {
    switch (mode) {
      case 'none':
        return `<div class="bg-warning/10 border border-warning/20 rounded-lg p-3">
                  <p class="text-sm text-gray-600">
                    <strong>No filtering:</strong> Users will see all records regardless of their ${scopeType}.
                  </p>
                  <p class="text-xs text-gray-400 mt-1">
                    This is appropriate for shared data like lookup tables, categories, or reference data that everyone should access.
                  </p>
                </div>`

      case 'named_scope':
        return this.renderNamedScopeConfig(scopeType, config)

      case 'where':
        return this.renderWhereConfig(scopeType, config)

      case 'advanced':
        return this.renderAdvancedConfig(scopeType, config)

      default:
        return ''
    }
  }

  renderNamedScopeConfig(scopeType, config) {
    const scopeName = typeof config === 'string' ? config : (config?.scope || config?.chain || '')
    const namedScopes = this.namedScopesValue || []

    return `
      <div class="form-control">
        <label class="label py-1">
          <span class="label-text text-xs font-medium">Choose a predefined filter</span>
        </label>
        <p class="text-xs text-gray-500 mb-2">These filters are defined in your application code and handle the filtering logic automatically.</p>
        <div class="flex gap-2">
          <select class="select select-bordered select-sm flex-1"
                  data-scope-type="${scopeType}"
                  data-field="scope_name"
                  data-action="change->scopes-editor#updateScope">
            <option value="">Select a filter...</option>
            ${namedScopes.map(s => `<option value="${s}" ${scopeName === s ? 'selected' : ''}>${s}</option>`).join('')}
          </select>
          <input type="text"
                 class="input input-bordered input-sm flex-1"
                 placeholder="${i18n.t('scopes_editor.or_type_filter_name')}"
                 value="${scopeName}"
                 data-scope-type="${scopeType}"
                 data-field="scope_name_custom"
                 data-action="input->scopes-editor#updateScope">
        </div>
      </div>
    `
  }

  renderWhereConfig(scopeType, config) {
    const whereConditions = config?.where || {}
    const chainScope = config?.chain || ''
    const columns = this.columnsValue || []

    // Common context values for the scope type
    const contextOptions = this.getContextOptionsForScopeType(scopeType)

    return `
      <div class="space-y-3">
        <div class="form-control">
          <label class="label py-1">
            <span class="label-text text-xs font-medium">Show records where...</span>
          </label>
          <p class="text-xs text-gray-500 mb-2">Users will only see records where this column matches their context.</p>
          <div class="flex gap-2 items-center flex-wrap">
            <select class="select select-bordered select-sm min-w-32"
                    data-scope-type="${scopeType}"
                    data-field="where_column"
                    data-action="change->scopes-editor#updateScope">
              <option value="">Select column...</option>
              ${columns.map(c => `<option value="${c}" ${Object.keys(whereConditions)[0] === c ? 'selected' : ''}>${c}</option>`).join('')}
            </select>
            <span class="text-gray-400 font-medium">equals</span>
            <select class="select select-bordered select-sm flex-1 min-w-40"
                    data-scope-type="${scopeType}"
                    data-field="where_value"
                    data-action="change->scopes-editor#updateScope">
              <option value="">Select what to match...</option>
              ${contextOptions.map(opt =>
                `<option value="${opt.value}" ${Object.values(whereConditions)[0] === opt.value ? 'selected' : ''}>${opt.label}</option>`
              ).join('')}
            </select>
          </div>
        </div>
        <div class="form-control">
          <label class="label py-1">
            <span class="label-text text-xs">Also apply additional filter (optional)</span>
          </label>
          <select class="select select-bordered select-sm"
                  data-scope-type="${scopeType}"
                  data-field="chain"
                  data-action="change->scopes-editor#updateScope">
            <option value="">None</option>
            ${(this.namedScopesValue || []).map(s => `<option value="${s}" ${chainScope === s ? 'selected' : ''}>${s}</option>`).join('')}
          </select>
        </div>
      </div>
    `
  }

  renderAdvancedConfig(scopeType, config) {
    const joins = config?.joins || ''
    const whereConditions = config?.where || {}
    const chainScope = config?.chain || ''
    const associations = this.associationsValue || []
    const columns = this.columnsValue || []
    const contextOptions = this.getContextOptionsForScopeType(scopeType)

    return `
      <div class="space-y-3">
        <div class="bg-info/10 border border-info/20 rounded-lg p-2 mb-2">
          <p class="text-xs text-gray-600">
            <strong>Advanced mode:</strong> Use this when the column you need to filter by is in a related table, not this one directly.
          </p>
        </div>

        <div class="form-control">
          <label class="label py-1">
            <span class="label-text text-xs font-medium">Step 1: Go through this connection</span>
          </label>
          <p class="text-xs text-gray-500 mb-2">First, tell us which related table has the column we need to filter on.</p>
          <select class="select select-bordered select-sm"
                  data-scope-type="${scopeType}"
                  data-field="joins"
                  data-action="change->scopes-editor#updateScope">
            <option value="">Select a connection...</option>
            ${associations.map(a => `<option value="${a.name}" ${joins === a.name ? 'selected' : ''}>${a.name} → ${a.target}</option>`).join('')}
          </select>
          <p class="text-xs text-gray-400 mt-1">Or type a path for nested connections: <code class="bg-base-200 px-1 rounded">neighbor.household</code></p>
          <input type="text"
                 class="input input-bordered input-sm mt-1"
                 placeholder="${i18n.t('scopes_editor.type_connection_path')}"
                 value="${joins}"
                 data-scope-type="${scopeType}"
                 data-field="joins_custom"
                 data-action="input->scopes-editor#updateScope">
        </div>

        <div class="form-control">
          <label class="label py-1">
            <span class="label-text text-xs font-medium">Step 2: Filter where...</span>
          </label>
          <p class="text-xs text-gray-500 mb-2">Now specify which column (on the connected table) to match against the user's context.</p>
          <div class="flex gap-2 items-center flex-wrap">
            <input type="text"
                   class="input input-bordered input-sm flex-1 min-w-32"
                   placeholder="${i18n.t('scopes_editor.example_scope')}"
                   value="${Object.keys(whereConditions)[0] || ''}"
                   data-scope-type="${scopeType}"
                   data-field="where_column_custom"
                   data-action="input->scopes-editor#updateScope">
            <span class="text-gray-400 font-medium">equals</span>
            <select class="select select-bordered select-sm flex-1 min-w-40"
                    data-scope-type="${scopeType}"
                    data-field="where_value"
                    data-action="change->scopes-editor#updateScope">
              <option value="">Select what to match...</option>
              ${contextOptions.map(opt =>
                `<option value="${opt.value}" ${Object.values(whereConditions)[0] === opt.value ? 'selected' : ''}>${opt.label}</option>`
              ).join('')}
            </select>
          </div>
        </div>

        <div class="form-control">
          <label class="label py-1">
            <span class="label-text text-xs">Also apply additional filter (optional)</span>
          </label>
          <select class="select select-bordered select-sm"
                  data-scope-type="${scopeType}"
                  data-field="chain"
                  data-action="change->scopes-editor#updateScope">
            <option value="">None</option>
            ${(this.namedScopesValue || []).map(s => `<option value="${s}" ${chainScope === s ? 'selected' : ''}>${s}</option>`).join('')}
          </select>
        </div>
      </div>
    `
  }

  getContextOptionsForScopeType(scopeType) {
    const baseOptions = [
      { value: 'context.organization_id', label: "The user's organization" },
      { value: 'context.network_id', label: "The user's network" },
      { value: 'context.user_id', label: "The user's ID" },
      { value: 'context.user', label: 'The current user (full record)' }
    ]

    // Add scope-specific recommended options at the top
    switch (scopeType) {
      case 'organization':
        return [
          { value: 'context.organization_id', label: "★ The user's organization (recommended)" },
          ...baseOptions.filter(o => o.value !== 'context.organization_id')
        ]
      case 'network':
        return [
          { value: 'context.network_id', label: "★ The user's network (recommended)" },
          ...baseOptions.filter(o => o.value !== 'context.network_id')
        ]
      case 'user':
        return [
          { value: 'context.user_id', label: "★ The user's ID (recommended)" },
          { value: 'context.user', label: '★ The current user (full record)' },
          ...baseOptions.filter(o => !o.value.startsWith('context.user'))
        ]
      default:
        return baseOptions
    }
  }

  changeScopeMode(event) {
    const scopeType = event.target.dataset.scopeType
    const mode = event.target.value

    // Reset the scope config based on mode
    switch (mode) {
      case 'none':
        this.scopes[scopeType] = null
        break
      case 'named_scope':
        this.scopes[scopeType] = ''
        break
      case 'where':
        this.scopes[scopeType] = { where: {} }
        break
      case 'advanced':
        this.scopes[scopeType] = { joins: '', where: {} }
        break
    }

    this.syncToHiddenField()

    // Re-render just this scope's details
    const target = this[`${scopeType}ScopeTarget`]
    if (target) {
      this.renderScopeConfig(target, scopeType, this.scopes[scopeType])
    }

    // Trigger validation for this scope
    this.validateCurrentScope(scopeType)
  }

  updateScope(event) {
    const scopeType = event.target.dataset.scopeType
    const field = event.target.dataset.field
    const value = event.target.value

    let config = this.scopes[scopeType]

    switch (field) {
      case 'scope_name':
      case 'scope_name_custom':
        // For named scope mode, set as string if simple, otherwise as object
        if (value) {
          this.scopes[scopeType] = value
        }
        break

      case 'where_column':
      case 'where_column_custom':
        if (typeof config !== 'object') config = { where: {} }
        if (!config.where) config.where = {}
        // Get the current value if there is one
        const currentValue = Object.values(config.where)[0] || ''
        config.where = value ? { [value]: currentValue } : {}
        this.scopes[scopeType] = config
        break

      case 'where_value':
        if (typeof config !== 'object') config = { where: {} }
        if (!config.where) config.where = {}
        const currentColumn = Object.keys(config.where)[0] || ''
        if (currentColumn) {
          config.where[currentColumn] = value
        }
        this.scopes[scopeType] = config
        break

      case 'chain':
        if (typeof config !== 'object') config = {}
        if (value) {
          config.chain = value
        } else {
          delete config.chain
        }
        this.scopes[scopeType] = config
        break

      case 'joins':
      case 'joins_custom':
        if (typeof config !== 'object') config = { where: {} }
        config.joins = value || undefined
        if (!value) delete config.joins
        this.scopes[scopeType] = config
        break
    }

    this.syncToHiddenField()

    // Trigger validation for this scope
    this.validateCurrentScope(scopeType)
  }

  syncToHiddenField() {
    // Clean up empty/null values but preserve mode indicators
    const cleanedScopes = {}
    Object.entries(this.scopes).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        cleanedScopes[key] = null
      } else if (typeof value === 'string' && value.trim() === '') {
        // Skip empty strings (unconfigured named_scope mode)
      } else if (typeof value === 'object') {
        const cleaned = { ...value }
        const isAdvancedMode = 'joins' in value

        // Remove empty where conditions (but keep the key if nothing else would indicate mode)
        if (cleaned.where && Object.keys(cleaned.where).length === 0) {
          delete cleaned.where
        }

        // For advanced mode, preserve the joins key even if empty to maintain mode
        // For non-advanced mode, remove empty joins
        if (!isAdvancedMode && cleaned.joins === '') {
          delete cleaned.joins
        }

        // Save if object has content, or if it's advanced mode (even with just empty joins)
        if (Object.keys(cleaned).length > 0) {
          cleanedScopes[key] = cleaned
        } else if (isAdvancedMode) {
          // Preserve advanced mode with minimal marker
          cleanedScopes[key] = { joins: '' }
        }
      } else {
        cleanedScopes[key] = value
      }
    })

    const jsonString = JSON.stringify(cleanedScopes, null, 2)
    this.hiddenFieldTarget.value = jsonString

    // Also update the preview textarea if it exists
    if (this.hasJsonPreviewTarget) {
      this.jsonPreviewTarget.value = jsonString
    }
  }

  // Handle manual JSON edits from the preview textarea
  syncFromTextarea(event) {
    try {
      const parsed = JSON.parse(event.target.value)
      this.scopes = parsed
      this.hiddenFieldTarget.value = JSON.stringify(parsed, null, 2)
      this.renderAllScopes()
      // Remove any error styling
      event.target.classList.remove('textarea-error')
    } catch {
      // Invalid JSON - show error styling but don't update
      event.target.classList.add('textarea-error')
    }
  }

  // Debounced validation on scope change
  validateCurrentScope(scopeType) {
    // Skip validation if no URL configured
    if (!this.hasValidateUrlValue) return

    // Clear existing timeout for this scope
    if (this.validationTimeouts[scopeType]) {
      clearTimeout(this.validationTimeouts[scopeType])
    }

    // Show pending state
    this.validationStates[scopeType] = { pending: true }
    this.updateValidationIndicator(scopeType)

    // Debounce the validation call
    this.validationTimeouts[scopeType] = setTimeout(() => {
      this.performValidation(scopeType)
    }, 500)
  }

  async performValidation(scopeType) {
    const config = this.scopes[scopeType]

    // Skip validation for 'none' mode
    if (config === null || config === undefined) {
      this.validationStates[scopeType] = null
      this.updateValidationIndicator(scopeType)
      return
    }

    try {
      const response = await fetch(this.validateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          scope_type: scopeType,
          config: typeof config === 'object' ? JSON.stringify(config) : config
        })
      })

      const result = await response.json()
      this.validationStates[scopeType] = result
      this.updateValidationIndicator(scopeType)
    } catch (error) {
      this.validationStates[scopeType] = { valid: false, error: error.message }
      this.updateValidationIndicator(scopeType)
    }
  }

  updateValidationIndicator(scopeType) {
    const indicator = this.element.querySelector(`[data-validation-indicator="${scopeType}"]`)
    if (indicator) {
      indicator.innerHTML = this.renderValidationIndicator(this.validationStates[scopeType])
    }
  }

  // Manual test button click
  testScope(event) {
    const scopeType = event.target.closest('[data-scope-type]').dataset.scopeType
    if (scopeType) {
      // Clear any pending timeout and immediately validate
      if (this.validationTimeouts[scopeType]) {
        clearTimeout(this.validationTimeouts[scopeType])
      }
      this.validationStates[scopeType] = { pending: true }
      this.updateValidationIndicator(scopeType)
      this.performValidation(scopeType)
    }
  }
}
